import { useEffect } from 'react';
import { getActionMapId } from '../utils/actionMapId';
import type { ActionEntity } from './useScopedActionForm';

const FIELD_FOR_KEY: Record<string, string> = {
  a: 'assignee',
  s: 'status',
  p: 'priority',
  d: 'target_date',
};

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    target.isContentEditable
  );
}

function openFieldOnRow(action: ActionEntity, field: string): boolean {
  const row = document.getElementById(`action-row-${getActionMapId(action)}`);
  const root = row?.closest('tr') ?? row?.parentElement ?? row;
  const trigger = root?.querySelector<HTMLElement>(
    `[data-action-field="${field}"]`
  );
  trigger?.click();
  return Boolean(trigger);
}

/**
 * Single-key row actions on the selected task: A assign, S status, P priority,
 * D due. They reuse the Phase 1 cell popovers by clicking the trigger already
 * in the row, so there is only one editor per field.
 */
export function useActionShortcuts(
  selected: ActionEntity[],
  enabled: boolean
): void {
  useEffect(() => {
    if (!enabled) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTypingTarget(event.target)) return;
      const field = FIELD_FOR_KEY[event.key.toLowerCase()];
      if (!field || selected.length === 0) return;
      if (openFieldOnRow(selected[0], field)) {
        event.preventDefault();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled, selected]);
}
